import { existsSync } from 'node:fs';
import path from 'node:path';
import type { PrismaClient } from '@prisma/client';
import cookieParser from 'cookie-parser';
import cors from 'cors';
import express from 'express';
import rateLimit from 'express-rate-limit';
import helmet from 'helmet';
import { pinoHttp } from 'pino-http';
import type { Config } from './core/config.js';
import { safeEqual } from './core/crypto.js';
import { errorHandler, HttpError, notFoundHandler } from './core/errors.js';
import { asyncHandler } from './core/http.js';
import { runDueJobs } from './core/jobs.js';
import type { Logger } from './core/logger.js';
import { openApiDocument } from './core/openapi.js';
import { createAccountingRouter } from './modules/accounting/accounting.routes.js';
import { createBankingRouter } from './modules/banking/banking.routes.js';
import { createAttachmentsRouter } from './modules/attachments/attachments.routes.js';
import { createCatalogRouter } from './modules/catalog/catalog.routes.js';
import { createCsvRouter } from './modules/csv/csv.routes.js';
import { createPartiesRouter } from './modules/parties/parties.routes.js';
import './modules/catalog/catalog.import.js';
import './modules/parties/parties.import.js';
import './modules/sales/sales.import.js';
import './modules/purchases/purchases.import.js';
import './modules/crm/crm.import.js';
import { runSalesSweeps } from './modules/sales/reminders.service.js';
import { createSalesRouter } from './modules/sales/documents.routes.js';
import { createPurchasesRouter } from './modules/purchases/purchases.routes.js';
import { createAutomationRouter } from './modules/automation/automation.routes.js';
import { createEfrisRouter } from './modules/efris/efris.routes.js';
import { createDashboardRouter } from './modules/dashboard/dashboard.routes.js';
import { createSearchRouter } from './modules/dashboard/search.routes.js';
import { createReconciliationRouter } from './modules/banking/reconciliation.routes.js';
import { createWhatsAppWebhookRouter } from './modules/messaging/whatsapp.cloud.js';
import { createPushRouter, pushPendingNotifications } from './modules/messaging/push.service.js';
import { createCrmRouter } from './modules/crm/crm.routes.js';
import { createPublicLeadsRouter } from './modules/crm/crm.public.routes.js';
import { createReportsRouter } from './modules/reports/reports.routes.js';
import { createSalesSendRouter } from './modules/sales/documents.send.routes.js';
import { createPublicRouter } from './modules/sales/public.routes.js';
import { createTasksRouter } from './modules/sales/tasks.routes.js';
import { createAuthRouter } from './modules/auth/auth.routes.js';
import { healthRouter } from './modules/health/health.routes.js';
import { createSettingsRouter } from './modules/settings/settings.routes.js';
import { createTeamRouter } from './modules/team/team.routes.js';

export interface AppDeps {
  config: Config;
  logger: Logger;
  db: PrismaClient;
  checkDb: () => Promise<boolean>;
}

export function createApp({ config, logger, db, checkDb }: AppDeps) {
  const app = express();
  app.disable('x-powered-by');
  app.set('trust proxy', 1);

  app.use(pinoHttp({ logger }));
  app.use(
    helmet({
      // The CSP differs by branch below (API vs. the optionally-served frontend), so it is set
      // per-branch instead of here. Every other helmet protection still applies to everything.
      contentSecurityPolicy: false,
      hsts:
        config.NODE_ENV === 'production' ? { maxAge: 31_536_000, includeSubDomains: true } : false,
    }),
  );
  // Provider webhooks read the raw body (signature check), so they sit before the JSON parser.
  app.use(createWhatsAppWebhookRouter({ db, config }));
  // The website lead form is called from other sites, so it answers before the app-wide CORS below.
  app.use(createPublicLeadsRouter({ db, config }));
  app.use(
    cors({
      origin: (origin, cb) => cb(null, !origin || config.CORS_ORIGINS.includes(origin)),
      credentials: true,
      allowedHeaders: ['Content-Type', 'Authorization', 'Idempotency-Key', 'X-Client'],
    }),
  );
  app.use(express.json({ limit: '1mb' }));
  app.use(cookieParser());
  app.use(
    rateLimit({
      windowMs: 60_000,
      limit: config.NODE_ENV === 'test' ? 100_000 : config.API_RATE_LIMIT,
      standardHeaders: 'draft-7',
      legacyHeaders: false,
    }),
  );

  const api = express.Router();
  // The API only ever serves JSON; lock its CSP down completely (moved from the global helmet
  // call so the optional static/SPA branch below can use a different, frontend-appropriate one).
  api.use((_req, res, next) => {
    res.setHeader('Content-Security-Policy', "default-src 'none'; frame-ancestors 'none'");
    next();
  });
  api.use(healthRouter(checkDb));
  api.get('/openapi.json', (_req, res) => res.json(openApiDocument));
  api.use('/auth', createAuthRouter({ db, config, logger }));
  api.use(createTeamRouter({ db, config }));
  api.use(createSettingsRouter({ db, config }));
  api.use(createAccountingRouter({ db, config }));
  api.use(createBankingRouter({ db, config }));
  api.use(createPartiesRouter({ db, config }));
  api.use(createCatalogRouter({ db, config }));
  api.use(createCsvRouter({ db, config }));
  api.use(createAttachmentsRouter({ db, config }));
  api.use(createPublicRouter({ db, config }));
  api.use(createSalesRouter({ db, config }));
  api.use(createSalesSendRouter({ db, config }));
  api.use(createPurchasesRouter({ db, config }));
  api.use(createReportsRouter({ db, config }));
  api.use(createCrmRouter({ db, config }));
  api.use(createAutomationRouter({ db, config }));
  api.use(createEfrisRouter({ db, config }));
  api.use(createPushRouter({ db, config }));
  api.use(createDashboardRouter({ db, config }));
  api.use(createSearchRouter({ db, config }));
  api.use(createReconciliationRouter({ db, config }));
  api.use(createTasksRouter({ db, config }));

  // Cron-callable job runner for hosts where a long-running process is not available (cPanel cron).
  api.post(
    '/system/cron',
    asyncHandler(async (req, res) => {
      const secret = req.header('x-cron-secret') ?? '';
      if (!safeEqual(secret, config.CRON_SECRET))
        throw new HttpError(403, 'FORBIDDEN', 'Invalid cron secret');
      const executed = await runDueJobs({ db, config, logger });
      const swept = await runSalesSweeps(db, new Date(), config);
      const pushed = await pushPendingNotifications(db, config);
      res.json({ executed, ...swept, pushed });
    }),
  );
  app.use('/api/v1', api);

  // Optional: serve the built web app from this same process (used on single-process hosts like
  // cPanel; a VPS behind nginx leaves WEB_DIST_DIR unset and serves web/dist itself instead).
  if (config.WEB_DIST_DIR && existsSync(config.WEB_DIST_DIR)) {
    const dist = config.WEB_DIST_DIR;
    const frontendCsp = (_req: express.Request, res: express.Response, next: express.NextFunction) => {
      res.setHeader(
        'Content-Security-Policy',
        "default-src 'self'; script-src 'self'; style-src 'self'; img-src 'self' data:; " +
          "font-src 'self'; connect-src 'self'; object-src 'none'; base-uri 'none'; " +
          "frame-ancestors 'none'; manifest-src 'self'",
      );
      next();
    };
    // Vite hashes every filename under /assets/, so those can be cached hard; everything else
    // (icons, manifest, the service worker) is small and should be revalidated on every request.
    app.use(
      '/assets',
      frontendCsp,
      express.static(path.join(dist, 'assets'), { maxAge: '1y', immutable: true }),
    );
    app.use(
      frontendCsp,
      (_req, res, next) => {
        res.setHeader('Cache-Control', 'no-cache');
        next();
      },
      express.static(dist, { index: false, cacheControl: false }),
    );
    // Client-side routes (React Router) all resolve to the same shell; the API and any file that
    // actually exists on disk were already handled above, so anything left here is a page route.
    app.get(/^(?!\/api\/).*/, frontendCsp, (_req, res) => {
      res.setHeader('Cache-Control', 'no-cache');
      res.sendFile(path.join(dist, 'index.html'));
    });
  }

  app.use(notFoundHandler);
  app.use(errorHandler(logger));
  return app;
}
